import * as React from "react";
import { CheckIcon, ChevronsUpDown } from "lucide-react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import "react-phone-number-input/style.css";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  // CommandInput, // ← enable if you want search
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

/**
 * Shadcn-styled PhoneInput wrapper around react-phone-number-input
 * - Popover country picker
 * - Flags via flags map
 * - Uses your shadcn Input for the number field
 */
const PhoneInput = React.forwardRef(function PhoneInput(
  { className, onChange, value, ...props },
  ref
) {
  return (
    <RPNInput.default
      ref={ref}
      className={cn("flex", className)}
      flagComponent={FlagComponent}
      countrySelectComponent={CountrySelect}
      inputComponent={InputComponent}
      smartCaret={false}
      value={value || undefined}
      onChange={(val) => onChange?.(val || "")}
      {...props}
    />
  );
});
PhoneInput.displayName = "PhoneInput";

const InputComponent = React.forwardRef(function InputComponent(
  { className, ...props },
  ref
) {
  return (
    <Input
      ref={ref}
      className={cn("rounded-e-lg rounded-s-none border-black border-2", className)}
      {...props}
    />
  );
});
InputComponent.displayName = "InputComponent";

const CountrySelect = ({ disabled, value: selectedCountry, options, onChange }) => {
  const scrollAreaRef = React.useRef(null);
  const [isOpen, setIsOpen] = React.useState(false);

  // If you want search later, add state + CommandInput from snippet.
  // const [searchValue, setSearchValue] = React.useState("");

  return (
    <Popover
      open={isOpen}
      modal
      onOpenChange={(open) => {
        setIsOpen(open);
        // open && setSearchValue("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "flex gap-2 rounded-e-none rounded-s-lg border-black border-2 border-r-0 px-3 focus:z-10",
            "h-10"
          )}
          disabled={disabled}
        >
          <FlagComponent country={selectedCountry} countryName={selectedCountry} />
          <span className="text-sm tabular-nums text-black/70">
            {selectedCountry ? `+${RPNInput.getCountryCallingCode(selectedCountry)}` : ""}
          </span>
          <ChevronsUpDown
            className={cn("-mr-1 size-4 opacity-60", disabled ? "hidden" : "opacity-100")}
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          {/* No search bar (as requested). If you want it back, uncomment CommandInput above and here. */}
          {/* <CommandInput placeholder="Search country..." /> */}

          <CommandList>
            <ScrollArea ref={scrollAreaRef} className="h-72">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {options.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={onChange}
                      onSelectComplete={() => setIsOpen(false)}
                    />
                  ) : null
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}) => {
  const handleSelect = () => {
    onChange(country);
    onSelectComplete();
  };

  return (
    <CommandItem className="gap-3" onSelect={handleSelect}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm truncate">{countryName}</span>
      <span className="text-sm text-black/60 tabular-nums">
        {`+${RPNInput.getCountryCallingCode(country)}`}
      </span>
      <CheckIcon className={cn("ml-auto size-4", country === selectedCountry ? "opacity-100" : "opacity-0")} />
    </CommandItem>
  );
};

const FlagComponent = ({ country, countryName }) => {
  const SvgFlag = country ? flags[country] : null;

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-black/10 [&_svg:not([class*='size-'])]:size-full">
      {SvgFlag ? <SvgFlag title={countryName} /> : null}
    </span>
  );
};

export { PhoneInput };
